// src/components/learning/StrandList.tsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getStrands } from "../../services/learningService";
import { FiLoader, FiAlertCircle, FiBook, FiGrid } from "react-icons/fi";

export interface Strand {
  id: string;
  name: string;
}

interface StrandListProps {
  subject: string;
  term: number;
  grade: string;
  onStrandSelect: (strand: string) => void;
}

const Container = styled.div`
  max-width: 800px;
  margin: 30px auto;
  padding: 30px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
`;

const Header = styled.div`
  margin-bottom: 24px;
  text-align: center;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  margin-bottom: 20px;
  font-size: 1.8rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 24px;
`;

const MetaContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const MetaTag = styled.span`
  background-color: ${({ theme }) => theme.primaryLight || "#f3e8ff"};
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StrandContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  justify-content: center;
`;

const StrandCard = styled.div`
  background-color: #fff;
  border: 1px solid ${({ theme }) => theme.primaryLight || "#e9d5ff"};
  border-radius: 10px;
  padding: 20px;
  cursor: pointer;
  font-size: 1.1rem;
  color: #333;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme }) => theme.primary || "#7e22ce"};
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const StrandIcon = styled(FiBook)`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  font-size: 1.3rem;
  flex-shrink: 0;
`;

const StrandName = styled.span`
  font-weight: 500;
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  gap: 16px;
`;

const SpinningIcon = styled(FiLoader)`
  animation: spin 1s linear infinite;
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  font-size: 2rem;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
  text-align: center;
`;

const ErrorIcon = styled(FiAlertCircle)`
  color: #e53e3e;
  font-size: 2.5rem;
`;

const ErrorMessage = styled.p`
  color: #e53e3e;
  font-size: 1.1rem;
  margin-bottom: 16px;
`;

const RetryButton = styled.button`
  background-color: ${({ theme }) => theme.primary || "#7e22ce"};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.primaryDark || "#5a17a7"};
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  text-align: center;
`;

const EmptyStateIcon = styled(FiGrid)`
  color: #a0aec0;
  font-size: 3rem;
`;

const EmptyStateMessage = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

export const StrandList: React.FC<StrandListProps> = ({ subject, term, grade, onStrandSelect }) => {
  const [strands, setStrands] = useState<Strand[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchStrands = async () => {
      setLoading(true);
      setError('');
      try {
        console.log("Fetching strands with:", { subject, term: term.toString(), grade });
        const data = await getStrands(subject, term.toString(), grade);
        console.log("Fetched strands:", data);
        setStrands(data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch strands. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStrands();
  }, [subject, term, grade]);

  const renderMetaInfo = () => (
    <MetaContainer>
      <MetaTag>
        <FiBook /> Subject: {subject}
      </MetaTag>
      <MetaTag>
        <span role="img" aria-label="Calendar">📅</span> Term: {term}
      </MetaTag>
      <MetaTag>
        <span role="img" aria-label="Grade Level">🎓</span> Grade: {grade}
      </MetaTag>
    </MetaContainer>
  );

  if (loading) {
    return (
      <Container>
        <LoaderContainer>
          <SpinningIcon />
          <p>Loading strands...</p>
        </LoaderContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <ErrorIcon />
          <ErrorMessage>{error}</ErrorMessage>
          <RetryButton onClick={() => window.location.reload()}>
            Try Again
          </RetryButton>
        </ErrorContainer>
      </Container>
    );
  }

  if (strands.length === 0) {
    return (
      <Container>
        <Header>
          <Title>No Strands Available</Title>
          {renderMetaInfo()}
        </Header>
        <EmptyStateContainer>
          <EmptyStateIcon />
          <EmptyStateMessage>
            No strands are available for {subject}, Term {term}, Grade {grade}.
          </EmptyStateMessage>
        </EmptyStateContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Select a Learning Strand</Title>
        <Subtitle>
          Please choose one of the following strands for {subject}, Term {term}, Grade {grade}.
        </Subtitle>
        {renderMetaInfo()}
      </Header>
      <StrandContainer>
        {strands.map((strand) => (
          <StrandCard
            key={strand.id}
            onClick={() => onStrandSelect(strand.name)}
          >
            <StrandIcon />
            <StrandName>{strand.name}</StrandName>
          </StrandCard>
        ))}
      </StrandContainer>
    </Container>
  );
};

export default StrandList;
